/**
 * nuud-rewards.js
 * Homegrown Rewards redeem handler — vanilla JS, no dependencies.
 * Posts to /apps/rewards/redeem (Cloudflare Worker, not yet deployed — endpoint is expected).
 * SYNC WARNING: TIER_COSTS must stay in sync with sections/nuud-rewards.liquid and
 * rewards-worker/src/tiers.js. Update all three if tier values change.
 */
(function () {
  'use strict';

  // Must match tiers.js and nuud-rewards.liquid exactly.
  var TIER_COSTS = {
    off5:  700,
    off10: 1400,
    off25: 3500,
    off35: 4900
  };

  var REDEEM_ENDPOINT = '/apps/rewards/redeem';
  var CODES_ENDPOINT  = '/apps/rewards/codes';

  // ── Helpers ──────────────────────────────────────────────────────

  function formatExpiry(isoString) {
    try {
      var d = new Date(isoString);
      return d.toLocaleDateString('en-US', {
        month: 'long',
        day:   'numeric',
        year:  'numeric'
      });
    } catch (e) {
      return isoString;
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getResultSlot() {
    return document.getElementById('nuud-rewards-result');
  }

  function getPtsDisplay() {
    return document.getElementById('nuud-pts-display');
  }

  // ── Balance / button state ────────────────────────────────────────

  function updateBalance(newBalance) {
    // Update the visible point number
    var el = getPtsDisplay();
    if (el) {
      el.textContent = newBalance;
    }

    // Re-evaluate every tier button
    var buttons = document.querySelectorAll('[data-tier]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var tierId = btn.getAttribute('data-tier');
      var cost = TIER_COSTS[tierId];
      if (cost === undefined) continue;

      if (newBalance >= cost) {
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('nrw__redeem-btn--disabled');
        btn.textContent = 'Redeem';
        // Remove gap hint if present
        var hint = btn.nextElementSibling;
        if (hint && hint.classList.contains('nrw__tier-gap-hint')) {
          hint.style.display = 'none';
        }
      } else {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('nrw__redeem-btn--disabled');
        // Update or restore gap hint
        var gapHint = btn.nextElementSibling;
        if (gapHint && gapHint.classList.contains('nrw__tier-gap-hint')) {
          gapHint.style.display = '';
          gapHint.textContent = 'Earn ' + (cost - newBalance) + ' more points to unlock';
        }
      }
    }
  }

  // ── Result slot rendering ─────────────────────────────────────────

  function clearResult() {
    var slot = getResultSlot();
    if (!slot) return;
    slot.hidden = true;
    slot.innerHTML = '';
  }

  function showResult(html) {
    var slot = getResultSlot();
    if (!slot) return;
    slot.innerHTML = html;
    slot.hidden = false;
    // Scroll into view gently — 'nearest' avoids jarring jumps
    try {
      slot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (e) {
      slot.scrollIntoView(false);
    }
  }

  function showError(msg, btn) {
    showResult('<p class="nrw__result-error">' + escapeHtml(msg) + '</p>');
    if (btn) {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('nrw__redeem-btn--disabled');
    }
  }

  function showSuccess(code, label, expires) {
    var expiryLine = expires ? ' Expires ' + escapeHtml(formatExpiry(expires)) + '.' : '';
    var html = [
      '<p class="nrw__result-label">Your ' + escapeHtml(label) + ' code is ready</p>',
      '<div class="nrw__code-row">',
      '  <span class="nrw__code-display" id="nrw-code-value">' + escapeHtml(code) + '</span>',
      '  <button class="nrw__copy-btn" type="button" aria-label="Copy discount code">Copy code</button>',
      '</div>',
      '<p class="nrw__result-meta">',
      '  Drop this code at checkout for ' + escapeHtml(label) + ' off your order.' + expiryLine,
      '</p>'
    ].join('');

    showResult(html);

    // Attach clipboard handler
    var copyBtn = document.querySelector('#nuud-rewards-result .nrw__copy-btn');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(function () {
            copyBtn.textContent = 'Copied!';
            setTimeout(function () { copyBtn.textContent = 'Copy code'; }, 2200);
          }).catch(function () {
            copyBtn.textContent = 'Select and copy';
          });
        } else {
          // Fallback: select the code element text
          var codeEl = document.getElementById('nrw-code-value');
          if (codeEl && window.getSelection) {
            var sel = window.getSelection();
            var range = document.createRange();
            range.selectNodeContents(codeEl);
            sel.removeAllRanges();
            sel.addRange(range);
          }
          copyBtn.textContent = 'Selected. Copy with Ctrl+C';
        }
      });
    }
  }

  // ── Redeem flow ───────────────────────────────────────────────────

  function handleRedeem(btn) {
    var tierId = btn.getAttribute('data-tier');
    var label  = btn.getAttribute('data-label') || tierId;

    // Disable the button and signal activity
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    var originalText = btn.textContent;
    btn.textContent = 'Working...';

    clearResult();

    fetch(REDEEM_ENDPOINT, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ tierId: tierId })
    })
      .then(function (res) {
        var status = res.status;
        return res.json().then(function (data) {
          return { ok: res.ok, status: status, data: data };
        });
      })
      .then(function (result) {
        if (result.ok && result.data && result.data.code) {
          // Success
          showSuccess(result.data.code, label, result.data.expires || null);
          if (typeof result.data.balance === 'number') {
            updateBalance(result.data.balance);
          }
          loadUnusedCodes(); // the fresh code should appear in the unused-codes card
          // Button stays disabled — a code was just minted; re-enabling handled by updateBalance
        } else {
          // Error from the worker
          var msg = (result.data && result.data.error)
            ? result.data.error
            : 'Something went wrong. Try again in a moment.';

          if (result.status === 401) {
            // Session expired — don't re-enable (page reload needed)
            var link = ' <a href="/account/login" class="nrw__result-link">Sign in here.</a>';
            showResult('<p class="nrw__result-error">' + escapeHtml(msg) + link + '</p>');
          } else {
            showError(msg, btn);
            btn.textContent = originalText;
          }
        }
      })
      .catch(function () {
        showError('Connection error. Check your internet and try again.', btn);
        btn.textContent = originalText;
      });
  }

  // ── Unused codes card ─────────────────────────────────────────────

  function loadUnusedCodes() {
    var card = document.getElementById('nuud-rewards-codes');
    var list = document.getElementById('nuud-rewards-codes-list');
    if (!card || !list) return;

    fetch(CODES_ENDPOINT, { method: 'GET' })
      .then(function (res) { return res.ok ? res.json() : { codes: [] }; })
      .then(function (data) {
        var codes = (data && data.codes) || [];
        if (!codes.length) {
          card.hidden = true;
          return;
        }
        var html = '';
        for (var i = 0; i < codes.length; i++) {
          var c = codes[i];
          html +=
            '<li class="nrw__codes-row">' +
              '<span class="nrw__codes-value">' + escapeHtml(c.value) + '</span>' +
              '<span class="nrw__code-display nrw__codes-code">' + escapeHtml(c.code) + '</span>' +
              '<span class="nrw__codes-expiry">Use by ' + escapeHtml(formatExpiry(c.expires)) + '</span>' +
              '<button type="button" class="nrw__copy-btn" data-copy-code="' + escapeHtml(c.code) + '">Copy code</button>' +
            '</li>';
        }
        list.innerHTML = html;
        card.hidden = false;
      })
      .catch(function () {
        // Silent — the card simply stays hidden
        card.hidden = true;
      });
  }

  function handleCopyCode(btn) {
    var code = btn.getAttribute('data-copy-code');
    if (!code) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(function () {
        btn.textContent = 'Copied!';
        setTimeout(function () { btn.textContent = 'Copy code'; }, 2200);
      }).catch(function () {
        btn.textContent = code;
      });
    } else {
      btn.textContent = code;
    }
  }

  // ── Init ──────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    var section = document.getElementById('nuud-rewards-section');
    if (!section) return;

    // Event delegation — catches buttons enabled after balance update too
    section.addEventListener('click', function (e) {
      var copyBtn = e.target.closest('[data-copy-code]');
      if (copyBtn) {
        handleCopyCode(copyBtn);
        return;
      }
      var btn = e.target.closest('[data-tier]');
      if (!btn) return;
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      handleRedeem(btn);
    });

    // Populate the unused-codes card (logged-in pages only render it)
    loadUnusedCodes();
  });

})();
